import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  circle?: boolean;
}

export function SkeletonBase({ className = '', width, height, circle = false }: SkeletonProps) {
  const style: React.CSSProperties = {};
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`animate-pulse bg-[#E5E7EB] ${circle ? 'rounded-full' : 'rounded-lg'} ${className}`}
      style={style}
    />
  );
}

/* ─── Text line skeletons ─── */

export function SkeletonText({ lines = 1, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBase
          key={i}
          width={i === lines - 1 && lines > 1 ? '70%' : '100%'}
          height={14}
          className="rounded-md"
        />
      ))}
    </div>
  );
}

/* ─── Card skeleton ─── */

export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 p-4 space-y-3 ${className}`}>
      <div className="flex items-center gap-3">
        <SkeletonBase width={36} height={36} circle />
        <div className="flex-1 space-y-1.5">
          <SkeletonBase width="60%" height={14} />
          <SkeletonBase width="40%" height={10} />
        </div>
      </div>
      <SkeletonText lines={2} />
    </div>
  );
}

/* ─── Project card skeleton (large with image) ─── */

export function SkeletonProjectCard({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${className}`}>
      <SkeletonBase height={208} className="rounded-none w-full" />
      <div className="px-4 py-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <SkeletonBase width="45%" height={14} />
          <SkeletonBase width="20%" height={14} />
        </div>
        <SkeletonBase width="100%" height={6} className="rounded-full" />
        <SkeletonBase width="50%" height={12} />
      </div>
    </div>
  );
}

/* ─── Stat row skeleton ─── */

export function SkeletonStatRow({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-${Math.min(count, 4)} gap-2 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="text-center space-y-1.5">
          <SkeletonBase width="50%" height={20} className="mx-auto" />
          <SkeletonBase width="70%" height={10} className="mx-auto" />
          <SkeletonBase width="60%" height={8} className="mx-auto" />
        </div>
      ))}
    </div>
  );
}

/* ─── Stage timeline skeleton ─── */

export function SkeletonStageTimeline({ count = 5, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="flex flex-col items-center flex-shrink-0 w-8">
            <SkeletonBase width={28} height={28} circle />
            {i < count - 1 && <SkeletonBase width={2} height={24} className="mt-1 rounded-full" />}
          </div>
          <div className="flex-1 min-w-0 space-y-1.5 pt-1">
            <div className="flex items-center justify-between">
              <SkeletonBase width="40%" height={14} />
              <SkeletonBase width="20%" height={14} />
            </div>
            <SkeletonBase width="55%" height={10} />
            <SkeletonBase width="35%" height={10} />
            {i === 1 && <SkeletonBase width="100%" height={6} className="rounded-full mt-1" />}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Photo grid skeleton ─── */

export function SkeletonPhotoGrid({ count = 4, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBase key={i} height={120} className="rounded-2xl w-full aspect-[4/3]" />
      ))}
    </div>
  );
}

/* ─── Table/list row skeleton ─── */

export function SkeletonListRow({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 py-3 ${className}`}>
      <SkeletonBase width={36} height={36} circle />
      <div className="flex-1 space-y-1.5">
        <SkeletonBase width="70%" height={14} />
        <SkeletonBase width="45%" height={10} />
      </div>
      <SkeletonBase width={60} height={24} className="rounded-full" />
    </div>
  );
}

/* ─── Hero banner skeleton ─── */

export function SkeletonHero({ className = '' }: { className?: string }) {
  return (
    <div className={`relative h-52 overflow-hidden ${className}`}>
      <SkeletonBase height="100%" className="rounded-none w-full" />
      <div className="absolute bottom-4 left-4 right-14 space-y-2">
        <SkeletonBase width="30%" height={10} />
        <SkeletonBase width="60%" height={24} />
        <SkeletonBase width="40%" height={12} />
      </div>
    </div>
  );
}

/* ─── Full page skeleton for project detail ─── */

export function SkeletonProjectDetail({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-0 ${className}`}>
      <SkeletonHero />
      <div className="bg-[#1A1A1A] px-4 py-3">
        <SkeletonStatRow count={4} />
      </div>
      <div className="px-4 py-4 space-y-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonStageTimeline count={3} />
      </div>
    </div>
  );
}

/* ─── Dashboard card grid skeleton ─── */

export function SkeletonDashboardGrid({ count = 2, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonProjectCard key={i} />
      ))}
    </div>
  );
}

/* ─── Compact list skeleton ─── */

export function SkeletonCompactList({ count = 3, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 p-3.5 flex items-center gap-3">
          <SkeletonBase width={48} height={48} className="rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <SkeletonBase width="60%" height={14} />
            <SkeletonBase width="40%" height={10} />
          </div>
          <SkeletonBase width={24} height={24} circle className="flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

/* ─── Progress bar skeleton ─── */

export function SkeletonProgressBar({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <SkeletonBase width="40%" height={14} />
        <SkeletonBase width="20%" height={14} />
      </div>
      <SkeletonBase width="100%" height={6} className="rounded-full" />
    </div>
  );
}